/**
 * LICITARS 3.0 - Routes Configuration with Role-Based Access
 * Integrates new navy/silver themed components while preserving existing functionality
 */

import React, { lazy } from "react";
import { RouteObject } from "react-router-dom";

// V3 Components (New Navy/Silver Theme)
import AppLayoutV3 from "@/components/layout/AppLayoutV3";
import LoginV3 from "@/components/auth/LoginV3";
import ProtectedRouteV3 from "@/components/auth/ProtectedRouteV3";
import UnauthorizedV3 from "@/pages/UnauthorizedV3";
import DashboardV3 from "@/pages/DashboardV3";
import ProcessListV3 from "@/pages/ProcessListV3";
import ProcessTimelineV3 from "@/pages/ProcessTimelineV3";
import NewProcessV3 from "@/pages/NewProcessV3";
import ProcessEditV3 from "@/pages/ProcessEditV3";
import Chat8V3 from "@/pages/Chat8V3";

// Legacy Components (Will be gradually updated)
const Home = lazy(() => import("./pages/Home"));
const Register = lazy(() => import("./pages/Register"));
const DocumentsV3 = lazy(() => import("./pages/DocumentsV3"));
const CreateDocument = lazy(() => import("./pages/CreateDocument"));
const Editor = lazy(() => import("./pages/Editor"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Library = lazy(() => import("./pages/Library"));
const Templates = lazy(() => import("./pages/Templates"));
const Metrics = lazy(() => import("./pages/Metrics"));
const Reports = lazy(() => import("./pages/Reports"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Help = lazy(() => import("./pages/Help"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DocumentsChat = lazy(() => import("./pages/DocumentsChat"));

/**
 * Routes Configuration V3
 * Features:
 * - New LoginV3 with navy/silver theme
 * - AppLayoutV3 with role-based navigation
 * - ProtectedRouteV3 with role detection
 * - Unauthorized page for access control
 */
export const routesV3: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/login",
    element: <LoginV3 />,
  },
  {
    path: "/register",
    element: <Register />,
  },
  {
    path: "/unauthorized",
    element: <UnauthorizedV3 />,
  },
  {
    path: "/",
    element: (
      <ProtectedRouteV3>
        <AppLayoutV3 />
      </ProtectedRouteV3>
    ),
    children: [
      // Dashboard (all roles) - V3 Navy Theme
      {
        index: true,
        element: <DashboardV3 />,
      },
      {
        path: "dashboard",
        element: <DashboardV3 />,
      },

      // Processes (all authenticated users)
      {
        path: "processes",
        children: [
          {
            index: true,
            element: <ProcessListV3 />,
          },
          {
            path: "new",
            element: <NewProcessV3 />,
          },
          {
            path: ":id",
            element: <ProcessTimelineV3 />,
          },
          {
            path: ":id/edit",
            element: <ProcessEditV3 />,
          },
        ],
      },

      // Documents (all authenticated users)
      {
        path: "documents",
        children: [
          {
            index: true,
            element: <DocumentsV3 />,
          },
          {
            path: "new",
            element: <CreateDocument />,
          },
          {
            path: "create",
            element: <CreateDocument />,
          },
          {
            path: ":id",
            element: <Editor />,
          },
          {
            path: ":id/chat",
            element: <DocumentsChat />,
          },
        ],
      },

      // Chat8 (all authenticated users)
      {
        path: "chat8",
        element: <Chat8V3 />,
      },
      {
        path: "chat8/:documentId",
        element: <Chat8V3 />,
      },

      // Calendar (all authenticated users)
      {
        path: "calendar",
        element: <Calendar />,
      },

      // Compliance (all authenticated users)
      {
        path: "compliance",
        element: <Compliance />,
      },

      // Library (all authenticated users)
      {
        path: "library",
        element: <Library />,
      },

      // Templates (all authenticated users)
      {
        path: "templates",
        element: <Templates />,
      },

      // Metrics (gestor, admin, master)
      {
        path: "metrics",
        element: (
          <ProtectedRouteV3 allowedRoles={["gestor", "admin", "master"]}>
            <Metrics />
          </ProtectedRouteV3>
        ),
      },

      // Reports (gestor, admin, master)
      {
        path: "reports",
        element: (
          <ProtectedRouteV3 allowedRoles={["gestor", "admin", "master"]}>
            <Reports />
          </ProtectedRouteV3>
        ),
      },

      // Notifications (all authenticated users)
      {
        path: "notifications",
        element: <Notifications />,
      },

      // Help (all authenticated users)
      {
        path: "help",
        element: <Help />,
      },

      // Master Routes (master only) - Future Backend
      // Uncomment when backend is ready
      /*
      {
        path: "master",
        children: [
          {
            path: "dashboard",
            element: (
              <ProtectedRouteV3 requiredRole="master">
                <MasterDashboard />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "clients",
            element: (
              <ProtectedRouteV3 requiredRole="master">
                <ClientManagement />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "pricing",
            element: (
              <ProtectedRouteV3 requiredRole="master">
                <CreditPricing />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "agents",
            element: (
              <ProtectedRouteV3 requiredRole="master">
                <AIAgentEditor />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "mapping",
            element: (
              <ProtectedRouteV3 requiredRole="master">
                <InheritanceMapping />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "parameters",
            element: (
              <ProtectedRouteV3 requiredRole="master">
                <LegalParameters />
              </ProtectedRouteV3>
            ),
          },
        ],
      },

      // Admin Routes (admin, master) - Future Backend
      {
        path: "admin",
        children: [
          {
            path: "users",
            element: (
              <ProtectedRouteV3 allowedRoles={["admin", "master"]}>
                <ClientAdmin />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "blocks",
            element: (
              <ProtectedRouteV3 allowedRoles={["admin", "master"]}>
                <ClientAdmin />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "knowledge",
            element: (
              <ProtectedRouteV3 allowedRoles={["admin", "master"]}>
                <ClientAdmin />
              </ProtectedRouteV3>
            ),
          },
          {
            path: "settings",
            element: (
              <ProtectedRouteV3 allowedRoles={["admin", "master"]}>
                <ClientAdmin />
              </ProtectedRouteV3>
            ),
          },
        ],
      },
      */
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routesV3;
